import { useState } from "react";
import { toast } from "sonner";
import { api, RESTAURANT } from "../lib/api";
import { MapPin, Phone, Mail, Clock, Instagram } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 pb-24">
      <section className="max-w-[1100px] mx-auto px-6 md:px-12">
        <div className="overline mb-6">Contact</div>
        <h1 className="font-display text-5xl sm:text-7xl text-[#F9F6F0] max-w-3xl">
          We are listening.
        </h1>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 md:px-12 mt-20 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5 space-y-10">
          <div className="flex gap-4">
            <MapPin className="w-4 h-4 text-[#D4C1A0] mt-1.5 flex-shrink-0" strokeWidth={1.2} />
            <div>
              <div className="overline mb-2">Visit</div>
              <p className="text-[#F9F6F0] leading-relaxed text-sm">{RESTAURANT.address}</p>
            </div>
          </div>
          <div className="flex gap-4">
            <Phone className="w-4 h-4 text-[#D4C1A0] mt-1.5 flex-shrink-0" strokeWidth={1.2} />
            <div>
              <div className="overline mb-2">Call</div>
              <p className="text-[#F9F6F0]">{RESTAURANT.phone}</p>
            </div>
          </div>
          <div className="flex gap-4">
            <Mail className="w-4 h-4 text-[#D4C1A0] mt-1.5 flex-shrink-0" strokeWidth={1.2} />
            <div>
              <div className="overline mb-2">Write</div>
              <p className="text-[#F9F6F0]">{RESTAURANT.email}</p>
            </div>
          </div>
          <div className="flex gap-4">
            <Clock className="w-4 h-4 text-[#D4C1A0] mt-1.5 flex-shrink-0" strokeWidth={1.2} />
            <div>
              <div className="overline mb-2">Hours</div>
              <p className="text-[#F9F6F0]">{RESTAURANT.hours}</p>
            </div>
          </div>

          <div className="aspect-[4/3] border border-[#2E3628] mt-10 overflow-hidden" data-testid="contact-map-wrap">
            <iframe
              data-testid="contact-map"
              title="CHICA location"
              src="https://www.google.com/maps?q=Pranav+Tower+Rajpur+Road+Dehradun&output=embed"
              className="w-full h-full"
              loading="lazy"
              style={{ filter: "grayscale(0.4) contrast(0.95)" }}
            />
          </div>
          <div className="mt-4 flex flex-wrap gap-4 text-xs uppercase tracking-widest">
            <a
              data-testid="contact-directions-btn"
              href={RESTAURANT.links.googleMaps}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#D4C1A0] hover:text-[#F9F6F0]"
            >
              Get directions →
            </a>
            <a
              data-testid="contact-whatsapp-btn"
              href={RESTAURANT.links.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#9AA894] hover:text-[#D4C1A0]"
            >
              WhatsApp →
            </a>
            <a
              data-testid="contact-instagram-btn"
              href={RESTAURANT.links.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#9AA894] hover:text-[#D4C1A0] inline-flex items-center gap-1"
            >
              <Instagram className="w-3 h-3" strokeWidth={1.4} /> @chicadoon
            </a>
          </div>
        </div>

        <div className="md:col-span-7">
          {sent ? (
            <div data-testid="contact-success" className="border border-[#2E3628] p-12">
              <div className="overline mb-4">Message received</div>
              <h2 className="font-display text-3xl text-[#F9F6F0]">Thank you. We will respond shortly.</h2>
              <button onClick={() => setSent(false)} className="btn-line mt-8" data-testid="contact-send-another">
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
              <div>
                <label className="overline">Name</label>
                <input data-testid="contact-name" required className="input-line mt-2" value={form.name} onChange={(e) => update("name", e.target.value)} />
              </div>
              <div>
                <label className="overline">Email</label>
                <input data-testid="contact-email" type="email" required className="input-line mt-2" value={form.email} onChange={(e) => update("email", e.target.value)} />
              </div>
              <div>
                <label className="overline">Subject</label>
                <input data-testid="contact-subject" className="input-line mt-2" value={form.subject} onChange={(e) => update("subject", e.target.value)} />
              </div>
              <div>
                <label className="overline">Message</label>
                <textarea data-testid="contact-message" required rows={5} className="input-line mt-2" value={form.message} onChange={(e) => update("message", e.target.value)} />
              </div>
              <button type="submit" disabled={loading} className="btn-line" data-testid="contact-submit-btn">
                {loading ? "Sending…" : "Send message"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
